<template>
  <div class="cost-prices-detail">
    <div class="mb-2 d-flex align-items-center gap-2">
      <router-link
        :to="{ name: 'cost-prices-unit-prices' }"
        class="d-flex align-items-center gap-2"
      >
        <feather-icon icon="ArrowLeftIcon" size="15" class="mr-1" />
        {{ t('Back to List') }}
      </router-link>
    </div>

    <div class="details-grid">
      <div class="detail-card">
        <h6 class="detail-label">{{ t('Provider') }}</h6>
        <template v-if="loading">
          <b-skeleton animation="wave" width="100%" height="24px" />
        </template>
        <p v-else class="detail-value">{{ details.provider.name }}</p>
      </div>

      <div class="detail-card">
        <h6 class="detail-label">{{ t('Package') }}</h6>
        <template v-if="loading">
          <b-skeleton animation="wave" width="100%" height="24px" />
        </template>
        <p v-else class="detail-value">
          {{ details.package.type }} - {{ details.package.description }}
        </p>
      </div>

      <div class="detail-card">
        <h6 class="detail-label">{{ t('Transport') }}</h6>
        <template v-if="loading">
          <b-skeleton animation="wave" width="100%" height="24px" />
        </template>
        <p v-else class="detail-value">
          {{ [details.transport.type, details.transport.direction].filter(Boolean).join(' - ') }}
        </p>
      </div>

      <div class="detail-card">
        <h6 class="detail-label">{{ t('Active Period') }}</h6>
        <template v-if="loading">
          <b-skeleton animation="wave" width="100%" height="24px" />
        </template>
        <p v-else class="detail-value">{{ details.active_at }} - {{ details.active_until }}</p>
      </div>
    </div>

    <template v-if="loading">
      <b-skeleton-table :rows="5" :columns="4" :table-props="{ bordered: true, striped: true }" />
    </template>
    <region-pricing-table-view v-else-if="tableData" :data="tableData" />
  </div>
</template>

<script>
import { BSkeleton, BSkeletonTable } from 'bootstrap-vue';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';
import RegionPricingTableView from '../../../providers/RegionPricingTableView.vue';

export default {
  name: 'CostPricesUnitDetail',
  components: {
    RegionPricingTableView,
    BSkeleton,
    BSkeletonTable
  },
  mixins: [helper],
  data() {
    return {
      tableData: null,
      details: {
        provider: {
          name: ''
        },
        package: {
          type: '',
          value: ''
        },
        transport: {
          type: '',
          direction: ''
        },
        active_at: '',
        active_until: ''
      },
      loading: false
    };
  },
  methods: {
    async getUnitPriceDetail() {
      this.loading = true;
      try {
        const response = await useService.pricingService.getUnitPriceDetail(
          this.$route.params.importId
        );
        this.tableData = {
          rows: response.data.rows,
          columns: response.data.columns
        };

        const [packagingTypesResponse, transportTypesResponse, directionTypesResponse] =
          await Promise.all([
            useService.getProviderRegionPricePackageType(),
            useService.constantService.getTransportType(),
            useService.constantService.getTransportDirection()
          ]);

        const packagingTypes = packagingTypesResponse.data;
        const transportTypes = transportTypesResponse.data;
        const directionTypes = directionTypesResponse.data;

        const detailData = response.data.details;

        this.details = {
          ...detailData,
          package: {
            ...detailData.package,
            type: detailData.package.type
              ? packagingTypes.find(
                  type => type.value.toString() === detailData.package.type.toString()
                )?.info
              : ''
          },
          transport: {
            type: detailData.transport.type
              ? transportTypes.find(
                  type => type.value.toString() === detailData.transport.type.toString()
                )?.info
              : '',
            direction: detailData.transport.direction
              ? directionTypes.find(
                  type => type.value.toString() === detailData.transport.direction.toString()
                )?.info
              : ''
          },
          active_at: this.$dayjs(detailData.active_at).format('DD.MM.YYYY HH:mm'),
          active_until: this.$dayjs(detailData.active_until).format('DD.MM.YYYY HH:mm')
        };
      } catch (error) {
        // console.error('Error fetching unit price detail:', error);
      } finally {
        this.loading = false;
      }
    }
  },
  created() {
    this.getUnitPriceDetail();
  },
  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style lang="scss" scoped>
.cost-prices-detail {
  .header-section {
    margin-bottom: 2rem;
  }

  .back-button {
    display: inline-flex;
    align-items: center;
  }

  .details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  .detail-card {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 0.5rem;
    padding: 1.25rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }

    .detail-label {
      color: #5e5873;
      font-size: 0.875rem;
      margin-bottom: 0.5rem;
      font-weight: 600;
    }

    .detail-value {
      color: #6e6b7b;
      font-size: 1rem;
      margin: 0;
      line-height: 1.5;
    }
  }

  .table-section {
    background: #fff;
    border-radius: 0.5rem;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
}

// Dark mode support
body.dark-layout {
  .cost-prices-detail {
    .detail-card {
      background: #283046;
      border-color: #404656;

      .detail-label {
        color: #b4b7bd;
      }

      .detail-value {
        color: #c3c6d4;
      }
    }

    .table-section {
      background: #283046;
    }
  }
}
</style>
